import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Pencil, Printer, RotateCcw } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { Button } from "../../components/ui/button";
import ToastMsg from "../../components/common/ToastMsg";
import apiClient from "../../lib/apiClient";
import {
  QUOTATION_DETAIL_URL, QUOTATION_TEMPLATE_URL, QUOTATION_MODULES_URL,
  QUOTATION_PDF_URL,
} from "../../constants/api";
import QuotationDocument from "./QuotationDocument";

// Edit-before-print. The document becomes typeable in place; figures and bank
// details stay locked so the paper always matches the saved quotation. Edits
// live only in this tab — change prices or plans in the quotation editor.
export function PrintToolbar({ title, onBack, editing, setEditing, onReset, extra }) {
  return (
    <div className="no-print sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 min-w-0">
        <button onClick={onBack} className="p-1 text-gray-500 hover:bg-gray-100 rounded"><ArrowLeft className="h-4 w-4" /></button>
        <span className="text-sm font-medium text-gray-900 truncate">{title}</span>
      </div>
      <div className="flex items-center gap-2">
        {editing && (
          <span className="hidden md:inline text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            Click any text to change it · prices &amp; bank details are locked · edits are not saved
          </span>
        )}
        <Button size="sm" variant={editing ? "default" : "outline"} onClick={() => setEditing(e => !e)}>
          <Pencil className="h-3.5 w-3.5 mr-1" /> {editing ? "Done editing" : "Edit before print"}
        </Button>
        <Button size="sm" variant="outline" onClick={onReset} title="Discard edits made on this page">
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
        {extra}
        <Button size="sm" onClick={() => { setEditing(false); setTimeout(() => window.print(), 50); }}>
          <Printer className="h-3.5 w-3.5 mr-1" /> Print
        </Button>
      </div>
    </div>
  );
}

export default function QuotationPrint() {
  const { id } = useParams();
  const nav = useNavigate();
  const { fetch } = useApiMethods();
  const [quote, setQuote]       = useState(null);
  const [template, setTemplate] = useState(null);
  const [modules, setModules]   = useState([]);
  const [editing, setEditing]   = useState(false);
  // Bumped to remount the document, which throws away in-place edits.
  const [docKey, setDocKey]     = useState(0);

  useEffect(() => {
    (async () => {
      const [q, m] = await Promise.all([
        fetch(QUOTATION_DETAIL_URL(id)),
        fetch(QUOTATION_MODULES_URL),
      ]);
      if (q?.status !== "success") return;
      setQuote(q.data);
      if (m?.status === "success") setModules(m.data || []);
      // The quotation's own project decides the branding.
      const t = await fetch(QUOTATION_TEMPLATE_URL, q.data.project_id ? { project_id: q.data.project_id } : {});
      if (t?.status === "success") setTemplate(t.data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const [downloading, setDownloading] = useState(false);
  const downloadPDF = async () => {
    if (!quote || downloading) return;
    setDownloading(true);
    try {
      // apiClient response interceptor returns response.data directly, so `data`
      // here IS the Blob — do NOT do `data.data`.
      const data = await apiClient.get(QUOTATION_PDF_URL(quote.id), { responseType: "blob", timeout: 120000 });
      const blob = data instanceof Blob ? data : new Blob([data], { type: "application/pdf" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url;
      a.download = `${quote.quote_number || "quotation"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error("PDF download failed", e);
      ToastMsg("error", "Failed to generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (!quote || !template) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  const reset = () => { setEditing(false); setDocKey(k => k + 1); };

  return (
    <div className="bg-gray-100 min-h-screen print:min-h-0 print:bg-white">
      <PrintToolbar
        title={`${quote.quote_number} · ${quote.customer_name}${quote.project_name ? ` · ${quote.project_name}` : ""}`}
        onBack={() => nav(`/quotations/${quote.id}`)}
        editing={editing} setEditing={setEditing} onReset={reset}
        extra={
          // The server PDF is laid out for eMedHub only (its logo and page
          // images), so it is offered only where the eMedHub default applies.
          // Every other project prints from this page — Print → Save as PDF.
          !template.is_project_template && (
            <Button size="sm" variant="outline" onClick={downloadPDF} disabled={downloading}>
              <Download className="h-3.5 w-3.5 mr-1" /> {downloading ? "Generating…" : "PDF"}
            </Button>
          )
        } />

      <QuotationDocument key={docKey} quote={quote} template={template} modules={modules} editable={editing} />
    </div>
  );
}
